import type { RedirectEntry, ServiceWidget } from '@frontend/types/adminhub';
import { defaultPublicIntegration, type PublicIntegrationSnapshot } from '../context/publicIntegrationTypes';
import { redirectEntriesFromManifest } from './redirectRegistryFromManifest';
import { getPortalLaunchHref } from './authPolicyRegistry';
import {
  resolveServiceUrl,
  tryResolveServiceUrl,
  type ServiceId,
} from './serviceUrlRegistry';

interface ServiceDefinition {
  id: string;
  /** Canonical registry id when different from widget id (e.g. collabra → collabora). */
  registryId?: ServiceId | string;
  title: string;
  description: string;
  launchLabel?: string;
  adminLabel?: string;
  wikiUrl?: string;
  swaggerUrl?: string;
  swaggerState: 'available' | 'planned' | 'unavailable';
  auth: string;
  showInSidebar: boolean;
  sidebarLabel?: string;
  /** Prefer registry launch / sso_entry when true (default). */
  hasLaunch?: boolean;
  /** Whether the service exposes a browser admin console. Default true. */
  webConsole?: boolean;
}

export interface SidebarServiceLink {
  id: string;
  label: string;
  href: string;
}

const SERVICE_DEFINITIONS: ServiceDefinition[] = [
  {
    id: 'keycloak',
    title: 'Keycloak',
    description: 'Identity, realms, clients, and SSO policies.',
    wikiUrl: 'https://www.keycloak.org/docs-api/latest/rest-api/index.html',
    swaggerUrl: '/api-docs/keycloak-swagger.html',
    swaggerState: 'available',
    auth: 'OIDC admin token / service account',
    showInSidebar: false,
    hasLaunch: false,
  },
  {
    id: 'openproject',
    title: 'OpenProject',
    description: 'Projects, members, workflows, and permissions.',
    launchLabel: 'Open Service',
    wikiUrl: 'https://www.openproject.org/docs/api/endpoints/',
    swaggerUrl: '/api-docs/openproject-swagger.html',
    swaggerState: 'available',
    auth: 'Basic auth (apikey token)',
    showInSidebar: true,
    sidebarLabel: 'OpenProject',
  },
  {
    id: 'gitea',
    title: 'Gitea',
    description: 'Repositories, organizations, users, and system administration.',
    launchLabel: 'Open Service',
    wikiUrl: 'https://docs.gitea.com/api/1.25/',
    swaggerUrl: '/api-docs/gitea-swagger.html',
    swaggerState: 'available',
    auth: 'Personal access token or OAuth2',
    showInSidebar: true,
    sidebarLabel: 'Gitea',
  },
  {
    id: 'woodpecker',
    title: 'Woodpecker CI',
    description: 'CI/CD pipelines, builds, and automation workflows.',
    launchLabel: 'Open CI',
    wikiUrl: 'https://woodpecker-ci.org/docs/development/openapi',
    swaggerState: 'unavailable',
    auth: 'Bearer token',
    showInSidebar: false,
  },
  {
    id: 'nextcloud',
    title: 'Nextcloud',
    description: 'Users, apps, storage, and collaboration settings.',
    launchLabel: 'Open Service',
    wikiUrl: 'https://docs.nextcloud.com/server/latest/developer_manual/client_apis/index.html',
    swaggerUrl: '/api-docs/nextcloud-swagger.html',
    swaggerState: 'available',
    auth: 'Basic auth + OCS APIRequest header',
    showInSidebar: true,
    sidebarLabel: 'Nextcloud',
  },
  {
    id: 'collabra',
    registryId: 'collabora',
    title: 'Collabra',
    description: 'Collaborative document editing integrated with Nextcloud.',
    launchLabel: 'Open Documents',
    wikiUrl: 'https://sdk.collaboraonline.com/docs/',
    swaggerState: 'unavailable',
    auth: 'Nextcloud OIDC session',
    showInSidebar: false,
    hasLaunch: false,
  },
  {
    id: 'vaultwarden',
    title: 'Vaultwarden',
    description: 'Password vault, SSO login surface, and operator admin panel.',
    launchLabel: 'Open Vault',
    adminLabel: 'Open Admin Panel',
    wikiUrl: 'https://github.com/dani-garcia/vaultwarden/wiki',
    swaggerState: 'unavailable',
    auth: 'OIDC SSO for users; admin token for vault admin',
    showInSidebar: true,
    sidebarLabel: 'Vaultwarden',
  },
  {
    id: 'coturn',
    title: 'Coturn',
    description: 'TURN/STUN relay service for voice/video sessions.',
    wikiUrl: 'https://github.com/coturn/coturn/wiki',
    swaggerState: 'unavailable',
    auth: 'Shared secret / TURN credentials',
    showInSidebar: false,
    hasLaunch: false,
    webConsole: false,
  },
  {
    id: 'netbird',
    title: 'NetBird',
    description: 'Self-hosted WireGuard mesh with Keycloak SSO and admin/user dashboard.',
    launchLabel: 'Open Mesh',
    adminLabel: 'Open Mesh Dashboard',
    swaggerState: 'unavailable',
    auth: 'Keycloak OIDC (NetBird dashboard + device enrollment)',
    showInSidebar: true,
    sidebarLabel: 'Mesh',
    hasLaunch: true,
    webConsole: true,
  },
];

function registryIdFor(definition: ServiceDefinition): string {
  return definition.registryId || definition.id;
}

function resolveLaunchUrl(
  definition: ServiceDefinition,
  origin: string,
): string | undefined {
  if (definition.hasLaunch === false) return undefined;
  const portalLaunch = getPortalLaunchHref(definition.id);
  if (portalLaunch) {
    return `${origin.replace(/\/$/, '')}${portalLaunch}`;
  }
  const rid = registryIdFor(definition);
  return (
    tryResolveServiceUrl(rid, 'launch', origin) ||
    tryResolveServiceUrl(rid, 'sso_entry', origin) ||
    tryResolveServiceUrl(rid, 'public', origin) ||
    undefined
  );
}

function resolveAdminUrl(definition: ServiceDefinition, origin: string): string | undefined {
  // Services without a browser admin console (e.g. coturn) must not render an
  // "Open Admin" button.
  if (definition.webConsole === false) return undefined;
  const rid = registryIdFor(definition);
  const admin = tryResolveServiceUrl(rid, 'admin', origin);
  if (admin) return admin;
  const pub = tryResolveServiceUrl(rid, 'public', origin);
  if (pub) return pub;
  return undefined;
}

function resolveApiBase(definition: ServiceDefinition, origin: string): string {
  const rid = registryIdFor(definition);
  const api = tryResolveServiceUrl(rid, 'api', origin);
  if (api) return api;
  return tryResolveServiceUrl(rid, 'public', origin) || '';
}

export function getServiceWidgets(
  origin: string,
  integration: PublicIntegrationSnapshot = defaultPublicIntegration
): ServiceWidget[] {
  const installed = Array.isArray(integration.installedServices)
    ? new Set(integration.installedServices)
    : null;

  return SERVICE_DEFINITIONS.filter((definition) => {
    if (definition.id === 'coturn' || definition.id === 'netbird') {
      return installed ? installed.has(definition.id) : false;
    }
    if (installed && installed.size > 0) {
      return installed.has(definition.id);
    }
    return true;
  }).map((definition) => ({
    id: definition.id,
    title: definition.title,
    description: definition.description,
    launchUrl: resolveLaunchUrl(definition, origin),
    launchLabel: definition.launchLabel,
    adminUrl: resolveAdminUrl(definition, origin),
    adminLabel: definition.adminLabel,
    wikiUrl: definition.wikiUrl,
    swaggerUrl: definition.swaggerUrl,
    swaggerState: definition.swaggerState,
    apiBase: resolveApiBase(definition, origin),
    auth: definition.auth,
  }));
}

export function getRedirectEntries(origin: string): RedirectEntry[] {
  return redirectEntriesFromManifest(origin);
}

export function getSidebarServiceLinks(
  origin: string,
  integration: PublicIntegrationSnapshot = defaultPublicIntegration
): SidebarServiceLink[] {
  const installed = Array.isArray(integration.installedServices)
    ? new Set(integration.installedServices)
    : null;

  return SERVICE_DEFINITIONS.filter((definition) => {
    if (!definition.showInSidebar) return false;
    if (definition.id === 'netbird') {
      return installed ? installed.has(definition.id) : false;
    }
    if (installed && installed.size > 0) {
      return installed.has(definition.id);
    }
    return true;
  })
    .map((definition) => {
      const href = resolveLaunchUrl(definition, origin);
      if (!href) return null;
      return {
        id: definition.id,
        label: definition.sidebarLabel || definition.title,
        href,
      };
    })
    .filter((x): x is SidebarServiceLink => x !== null);
}

/** Resolve a platform service URL by registry reference (widgets / pages). */
export function serviceUrl(id: string, role: 'public' | 'admin' | 'api' | 'sso_entry' | 'launch' | 'callback' | 'landing', origin: string): string {
  return resolveServiceUrl(id, role, origin);
}
